import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScopeUnit } from "../../util/slopifyScopeMap";

interface ScopeMentionProps {
  units: ScopeUnit[];
  modelName: string;
  query: string;
  onSelect: (unit: ScopeUnit) => void;
  onClose: () => void;
}

export const ScopeMention = ({ units, modelName, query, onSelect, onClose }: ScopeMentionProps) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const labelFor = (u: ScopeUnit) =>
    t(
      `general.assistantScenarIa.scope.units.${modelName}.${u.labelKey}`,
      u.labelParams,
    ) as string;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return units;
    return units.filter((u) => labelFor(u).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, query, modelName]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) =>
          filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const pick = filtered[activeIndex];
        if (pick) onSelect(pick);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [filtered, activeIndex, onSelect, onClose]);

  if (filtered.length === 0) {
    return (
      <div
        ref={containerRef}
        className="absolute bottom-full left-0 mb-1 z-20 bg-white border border-gray-200 rounded-md shadow-md py-1 w-48 text-[11px] text-gray-500 px-2"
      >
        {t("general.assistantScenarIa.scopeMentionEmpty")}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 mb-1 z-20 bg-white border border-gray-200 rounded-md shadow-md py-1 w-48 max-h-48 overflow-y-auto"
    >
      {filtered.map((u, idx) => (
        <button
          key={u.id}
          type="button"
          className={`w-full text-left px-2 py-1 text-[11px] ${
            idx === activeIndex
              ? "bg-green-100 text-green-700"
              : "hover:bg-green-50 hover:text-green-700"
          }`}
          onMouseEnter={() => setActiveIndex(idx)}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(u);
          }}
        >
          {labelFor(u)}
        </button>
      ))}
    </div>
  );
};

export default ScopeMention;
